import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import visionLiveLogo from '../assets/images/visionlive.png';
import tryItOutIcon from '../assets/images/try-it-out-icon.png';

/**
 * Main site header/navbar used on landing and blog pages.
 * Props: onOpenDemo, onOutfitClick, onBlogClick, onLoginClick, onLogoClick (optional – when set, logo is clickable)
 */
const SiteHeader = ({ onOpenDemo, onOutfitClick, onBlogClick, onLoginClick, onLogoClick }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const closeMenu = () => setMobileMenuOpen(false);
  const handleAnchorClick = () => closeMenu();
  const handleBlogClick = () => { onBlogClick?.(); closeMenu(); };
  const handleOutfitClick = () => { onOutfitClick?.(); closeMenu(); };
  const handleLoginClick = () => { onLoginClick?.(); closeMenu(); };
  const handleDemoClick = () => { onOpenDemo?.(); closeMenu(); };

  return (
    <motion.nav
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="sticky top-0 z-50 bg-[#0d0d0d] pt-[env(safe-area-inset-top)] md:fixed md:left-0 md:right-0 md:bg-[#0d0d0d]/90 md:backdrop-blur-md"
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-2">
          {onLogoClick ? (
            <button
              type="button"
              onClick={onLogoClick}
              className="flex items-center gap-2 hover:opacity-90 transition-opacity"
              aria-label="Home"
            >
              <img
                src={visionLiveLogo}
                alt="VisionLive"
                className="h-9 sm:h-10 w-auto max-w-[220px] object-contain"
              />
            </button>
          ) : (
            <img
              src={visionLiveLogo}
              alt="VisionLive"
              className="h-9 sm:h-10 w-auto max-w-[220px] object-contain"
            />
          )}
        </div>

        {/* Nav Links - desktop */}
        <div className="hidden md:flex items-center gap-8">
          <a href="/#features" className="text-sm font-medium text-white/60 hover:text-white transition-colors">
            Features
          </a>
          <a href="/#how-it-works" className="text-sm font-medium text-white/60 hover:text-white transition-colors">
            How it Works
          </a>
          <a href="/#pricing" className="text-sm font-medium text-white/60 hover:text-white transition-colors">
            Pricing
          </a>
          <a href="/#partnership" className="text-sm font-medium text-white/60 hover:text-white transition-colors">
            Partner Launch
          </a>
          <a href="/#faq" className="text-sm font-medium text-white/60 hover:text-white transition-colors">
            FAQ
          </a>
          {onBlogClick && (
            <button
              type="button"
              onClick={onBlogClick}
              className="text-sm font-medium text-white/60 hover:text-white transition-colors"
            >
              Blog
            </button>
          )}
          {onOutfitClick && (
            <button
              type="button"
              onClick={onOutfitClick}
              className="text-sm font-medium text-white/60 hover:text-white transition-colors flex items-center gap-2"
            >
              <span className="px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider bg-violet-500/20 text-violet-400 rounded-full border border-violet-500/30">
                Outfit
              </span>
            </button>
          )}
        </div>

        {/* CTA Buttons + Hamburger */}
        <div className="flex items-center gap-3">
          {onLoginClick && (
            <button
              type="button"
              onClick={onLoginClick}
              className="hidden sm:block px-4 py-2 text-sm font-medium text-white/60 hover:text-white transition-colors"
            >
              Log in
            </button>
          )}
          <a
            href="/auth"
            className="hidden sm:block px-5 py-2.5 rounded-full border border-white/20 text-sm font-semibold text-white hover:border-white/40 hover:bg-white/5 transition-all"
          >
            Get started
          </a>
          {onOpenDemo && (
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onOpenDemo}
              className="hidden md:block px-5 py-2.5 rounded-full bg-[#3B82F6] text-sm font-semibold text-white hover:bg-[#2563EB] transition-all"
            >
              Try it out
            </motion.button>
          )}
          {onOpenDemo && (
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onOpenDemo}
              className="md:hidden p-2.5 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] transition-all flex items-center justify-center"
              aria-label="Try it out"
            >
              <img
                src={tryItOutIcon}
                alt=""
                className="w-5 h-5 object-contain brightness-0 invert"
              />
            </motion.button>
          )}
          <button
            type="button"
            onClick={() => setMobileMenuOpen((o) => !o)}
            className="md:hidden p-2 -mr-2 text-white/80 hover:text-white transition-colors rounded-lg hover:bg-white/5"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden overflow-hidden border-t border-white/10 bg-[#0d0d0d]"
          >
            <div className="px-6 py-4 pb-6 flex flex-col gap-1">
              <a href="/#features" onClick={handleAnchorClick} className="py-3 text-sm font-medium text-white/60 hover:text-white transition-colors">Features</a>
              <a href="/#how-it-works" onClick={handleAnchorClick} className="py-3 text-sm font-medium text-white/60 hover:text-white transition-colors">How it Works</a>
              <a href="/#pricing" onClick={handleAnchorClick} className="py-3 text-sm font-medium text-white/60 hover:text-white transition-colors">Pricing</a>
              <a href="/#partnership" onClick={handleAnchorClick} className="py-3 text-sm font-medium text-white/60 hover:text-white transition-colors">Partner Launch</a>
              <a href="/#faq" onClick={handleAnchorClick} className="py-3 text-sm font-medium text-white/60 hover:text-white transition-colors">FAQ</a>
              {onBlogClick && (
                <button type="button" onClick={handleBlogClick} className="py-3 text-left text-sm font-medium text-white/60 hover:text-white transition-colors">Blog</button>
              )}
              {onOutfitClick && (
                <button type="button" onClick={handleOutfitClick} className="py-3 text-left text-sm font-medium text-white/60 hover:text-white transition-colors flex items-center gap-2">
                  <span className="px-2 py-0.5 text-[12px] font-bold uppercase tracking-wider bg-violet-500/20 text-violet-400 rounded-full border border-violet-500/30">Outfit</span>
                </button>
              )}
              <div className="mt-4 pt-4 border-t border-white/10 flex flex-col gap-2">
                {onLoginClick && (
                  <button type="button" onClick={handleLoginClick} className="py-3 text-left text-sm font-medium text-white/60 hover:text-white transition-colors">Log in</button>
                )}
                <a href="/auth" onClick={closeMenu} className="w-full py-3.5 rounded-full border border-white/20 text-sm font-semibold text-white hover:border-white/40 hover:bg-white/5 transition-all text-center">Get started</a>
                {onOpenDemo && (
                  <button type="button" onClick={handleDemoClick} className="w-full py-3.5 rounded-full bg-[#3B82F6] text-sm font-semibold text-white hover:bg-[#2563EB] transition-colors text-center">Try it out</button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default SiteHeader;
